import React, { useRef, useCallback, useState } from 'react';
import styled from 'styled-components';

interface Strip { src: string; label: string; }
interface Props  { strips: Strip[]; }

const Wrapper = styled.div`
  width: 100%;
  border-radius: 8px;
  overflow: hidden;
  border: 1px solid var(--card-border);
`;

const Gallery = styled.div`
  position: relative;
  width: 100%;
  aspect-ratio: 1 / 1;
  overflow: hidden;
  cursor: pointer;
  background: #000;
`;

const Panel = styled.div`
  position: absolute;
  inset: 0;
  transition: clip-path 600ms ease;
`;

const PanelImg = styled.img`
  width: 100%;
  height: 100%;
  object-fit: cover;
  display: block;
`;

const DividerLine = styled.div`
  position: absolute;
  top: 0;
  bottom: 0;
  width: 1px;
  background: rgba(255, 255, 255, 0.2);
  transform: translateX(-50%);
  transition: left 600ms ease;
  pointer-events: none;
  z-index: 10;
`;

const LabelRow = styled.div`
  display: flex;
  border-top: 1px solid var(--card-border);
`;

const LabelBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  background: ${p => p.$active ? 'rgba(128,128,128,0.12)' : 'var(--card-bg)'};
  border: none;
  border-left: 1px solid var(--card-border);
  padding: 8px 4px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 12px;
  letter-spacing: 0.07em;
  text-transform: uppercase;
  color: var(--text);
  opacity: ${p => p.$active ? 1 : 0.5};
  cursor: pointer;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  transition: background 0.2s, opacity 0.2s;
  &:first-child { border-left: none; }
`;

const ProgressStrips: React.FC<Props> = ({ strips }) => {
  const [activeIndex, setActiveIndex] = useState<number | null>(null);
  const panelRefs   = useRef<(HTMLDivElement | null)[]>([]);
  const dividerRefs = useRef<(HTMLDivElement | null)[]>([]);
  const n = strips.length;

  const setSplitMode = useCallback(() => {
    setActiveIndex(null);
    panelRefs.current.forEach((el, i) => {
      if (!el) return;
      const start = (i / n) * 100;
      const end   = ((i + 1) / n) * 100;
      el.style.clipPath = `polygon(${start}% 0, ${end}% 0, ${end}% 100%, ${start}% 100%)`;
    });
    dividerRefs.current.forEach((el, i) => {
      if (!el) return;
      el.style.left = `${((i + 1) / n) * 100}%`;
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const setExpandedMode = useCallback((index: number) => {
    setActiveIndex(index);
    panelRefs.current.forEach((el, i) => {
      if (!el) return;
      if (i === index) {
        el.style.clipPath = 'polygon(0 0, 100% 0, 100% 100%, 0 100%)';
      } else {
        const c = i < index ? 0 : 100;
        el.style.clipPath = `polygon(${c}% 0, ${c}% 0, ${c}% 100%, ${c}% 100%)`;
      }
    });
    dividerRefs.current.forEach((el, i) => {
      if (!el) return;
      el.style.left = (i + 1) <= index ? '-2%' : '102%';
    });
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const toggle = useCallback((index: number) => {
    if (activeIndex === index) {
      setSplitMode();
    } else {
      setExpandedMode(index);
    }
  }, [activeIndex, setSplitMode, setExpandedMode]);

  // Set initial split clip-paths after first render
  const galleryRef = useRef<HTMLDivElement | null>(null);
  const initialized = useRef(false);
  const initRef = useCallback((el: HTMLDivElement | null) => {
    galleryRef.current = el;
    if (el && !initialized.current) {
      initialized.current = true;
      // Defer so all panel/divider refs are populated
      requestAnimationFrame(() => setSplitMode());
    }
  }, [setSplitMode]);

  return (
    <Wrapper>
      <Gallery ref={initRef}>
        {strips.map(({ src }, i) => (
          <Panel
            key={i}
            ref={el => { panelRefs.current[i] = el; }}
            onClick={() => toggle(i)}
          >
            <PanelImg src={src} alt="" />
          </Panel>
        ))}

        {Array.from({ length: n - 1 }, (_, i) => (
          <DividerLine
            key={i}
            ref={el => { dividerRefs.current[i] = el; }}
          />
        ))}
      </Gallery>

      <LabelRow>
        {strips.map(({ label }, i) => (
          <LabelBtn key={i} $active={activeIndex === i} onClick={() => toggle(i)}>
            {label}
          </LabelBtn>
        ))}
      </LabelRow>
    </Wrapper>
  );
};

export default ProgressStrips;
